import CancelablePromise from '@ms/odsp-utilities/lib/async/Promise';
import Async from '@ms/odsp-utilities/lib/async/Async';
import Retry from '@ms/odsp-utilities/lib/async/Retry';
import QosEvent, { ResultTypeEnum } from '@ms/odsp-utilities/lib/logging/events/Qos.event';
import ApiEvent, { IApiEndSchema } from '@ms/odsp-utilities/lib/logging/events/Api.event';
import RUMOneLogger from '@ms/odsp-utilities/lib/logging/rumone/RUMOneLogger';
import ServerData from './ServerData';
import { IErrorData } from './ServerData';
import ServerConnection from './ServerConnection';
import ISpPageContext from '../../interfaces/ISpPageContext';
import { IQosEndSchema } from '@ms/odsp-utilities/lib/logging/events/Qos.event';
import { Killswitch } from '@ms/odsp-utilities/lib/killswitch/Killswitch';

export type IDataRequestorPageContext = Pick<ISpPageContext,
    'webServerRelativeUrl' |
    'webAbsoluteUrl' |
    'updateFormDigestPageLoaded' |
    'serverTime' |
    'formDigestValue' |
    'formDigestTimeoutSeconds' |
    'authToken' |
    'getAuthToken'>;

export interface IDataRequestorParams {
    /**
     * The base QoS name to use for all requests.
     * Calls to `getData` will append their own provided `qosName` values to this base value.
     */
    qosName?: string;
}

export interface IDataRequestorLegacyParams extends IDataRequestorParams {
    /**
     * A current page context. Prefer passing this value in dependencies.
     * `DataRequestor` minimally requires `webServerRelativeUrl` to be set.
     * Additionally, settings `updateFormDigestPageLoaded` can help avoid extra server calls.
     */
    pageContext?: IDataRequestorPageContext;
}

export interface IDataRequestorDependencies {
    /**
     * A current page context.
     * `DataRequestor` minimally requires `webServerRelativeUrl` to be set.
     * Additionally, settings `updateFormDigestPageLoaded` can help avoid extra server calls.
     */
    pageContext?: IDataRequestorPageContext;
    qosEvent?: typeof QosEvent;
    apiEvent?: typeof ApiEvent;
}

export interface IDataRequestor {
    getData<T>(options: IDataRequestGetDataOptions<T>): CancelablePromise<T>;
}

export { IErrorData };

export interface IDataRequestGetDataOptions<T> {
    url: string;
    /**
     * Optional override for parsing the server response.
     * The default behavior to parse as JSON and cast to `T`.
     * Only override this to handle the response string directly or convert to a different data type.
     */
    parseResponse?: (responseText: string, serverData: ServerData) => T;
    /**
     * The name for the call for QoS reporting.
     */
    qosName: string;
    /**
    * Data to be included in the qos end event for this request.
    */
    qosExtraData?: IQosEndSchema['extraData'];
    /**
    * Optional handler to decide proper result code for QOS event objects
    *
    * @returns {string} result code to be set on the QOS event object associated with this operation.
    */
    qosHandler?: (errorData: IErrorData) => string;

    /**
    * Optional QOS logging function by consuming the data requestor qos data, currently used for app level QOS logging
    */
    customQosLogger?: (qosData: IQosEndSchema) => void;
    /**
     * The HTTP method to use for the request.
     */
    method?: string;
    /**
     * Data to put in the body of thr request.
     * Objects should be serialized to JSON first.
     */
    additionalPostData?: string | Blob;
    /**
     * Additional headers to include in the request.
     * Headers minimally necessary for SharePoint calls will be provided automatically.
     */
    additionalHeaders?: { [key: string]: string };
    /**
     * The request content type.
     * Default is 'application/json;odata=verbose'.
     */
    contentType?: string;
    /**
     * The maximum number of retries to make in response to retriable errors.
     * Default is 0.
     */
    maxRetries?: number;
    /**
     * Whether or not to disable automatic redirects to the login page if the session expired.
     */
    noRedirect?: boolean;
    /**
     * Whether or not this call is being made across a site collection.
     * Unnecessary if the full URL is provided.
     */
    crossSiteCollectionCall?: boolean;
    /**
     * The expected response type.
     */
    responseType?: string;
    /**
     * Specify whether or not up-to-date digest information must be provided for this request.
     */
    needsRequestDigest?: boolean;
    /**
     * A handler for upload progress events.
     */
    onUploadProgress?: (event: ProgressEvent) => void;
    /**
     * timeout in milliseconds before cancelling the request promise
     */
    timeout?: number;
    /**
     * An auth token to use for the request.
     */
    authToken?: string;
    /**
     * specify whether or not timeout is also applied to last retry, default is false.
     * Set it to false when not doing timeout for the last retry to guarantee data can be received regardless performance
     */
    noTimeoutInLastRetry?: boolean;
}

export interface IDataRequestGetDataAsBlobOptions extends IDataRequestGetDataOptions<Blob> {
    responseType: 'blob';
}

export class DataRequestor implements IDataRequestor {
    private _pageContext: IDataRequestorPageContext | undefined;

    private _qosName: string;

    private _async: Async;

    private _qosEvent: typeof QosEvent;
    private _apiEvent: typeof ApiEvent;

    public static parseJSON<T>(responseText: string): T {
        return responseText && JSON.parse(responseText) || undefined;
    }

    constructor(params: IDataRequestorLegacyParams);
    constructor(params: IDataRequestorParams, dependencies: IDataRequestorDependencies);
    constructor(params: IDataRequestorParams, dependencies?: IDataRequestorDependencies) {
        this._pageContext = dependencies ? dependencies.pageContext : (params as IDataRequestorLegacyParams).pageContext;
        this._qosName = params.qosName;
        this._qosEvent = dependencies && dependencies.qosEvent || QosEvent;
        this._apiEvent = dependencies && dependencies.apiEvent || ApiEvent;
        this._async = new Async(this);
    }

    public getData(options: IDataRequestGetDataAsBlobOptions): CancelablePromise<Blob>;
    public getData<T>(options: IDataRequestGetDataOptions<T>): CancelablePromise<T>;
    public getData<T>({
        url,
        parseResponse = <(responseText: string, serverData: ServerData) => T>DataRequestor.parseJSON,
        qosName,
        qosExtraData,
        qosHandler,
        customQosLogger,
        additionalPostData = '',
        additionalHeaders,
        contentType,
        method = 'POST',
        maxRetries = 0,
        crossSiteCollectionCall = false,
        noRedirect = false,
        needsRequestDigest = true,
        responseType,
        onUploadProgress,
        timeout,
        authToken: specifiedAuthToken,
        noTimeoutInLastRetry = false
    }: IDataRequestGetDataOptions<T>): CancelablePromise<T | Blob> {
        maxRetries = Math.max(maxRetries, 0);
        let numRetries: number = 0;

        const pageContext = this._pageContext;

        const getDataPromise = (authToken?: string): CancelablePromise<T | Blob> => {

            // Add Authorization header to request if authToken exists on _spPageContext
            // and an Authorization header doesn't already exist on the request.
            if (authToken) {
                const authorizationHeaderValue = `Bearer ${authToken}`;

                // Add Authorization header if it doesn't exist already.
                if (additionalHeaders) {
                    let authorizationHeaderSpecified = false;
                    for (const headerKey in additionalHeaders) {
                        if (headerKey) {
                            if (headerKey.toLowerCase() === 'authorization') {
                                authorizationHeaderSpecified = true;
                            }
                        }
                    }

                    if (!authorizationHeaderSpecified) {
                        additionalHeaders['Authorization'] = authorizationHeaderValue;
                    }
                } else {
                    additionalHeaders = {
                        'Authorization': authorizationHeaderValue
                    };
                }

                // Since Authorization header is present, the request is cross-origin.
                crossSiteCollectionCall = true;
                needsRequestDigest = false;
                noRedirect = true;
            }

            let serverConnection = new ServerConnection({
                ...(pageContext ? {
                    webServerRelativeUrl: pageContext.webServerRelativeUrl,
                    webAbsoluteUrl: crossSiteCollectionCall ? pageContext.webAbsoluteUrl : undefined,
                    formDigest: {
                        // Pull current digest state from the page.
                        // This helps initial requests avoid extra server calls.
                        // Unit tests will need to spoof this in order to avoid the need to mock the digest response.
                        serverTime: pageContext.serverTime,
                        updateFormDigestPageLoaded: pageContext.updateFormDigestPageLoaded,
                        formDigestValue: pageContext.formDigestValue,
                        formDigestTimeoutSeconds: pageContext.formDigestTimeoutSeconds
                    }
                } : {}),
                needsRequestDigest: needsRequestDigest
            });

            let qosNames: string[] = [];

            if (this._qosName) {
                qosNames.push(this._qosName);
            }

            if (qosName) {
                qosNames.push(qosName);
            }

            qosName = qosNames.join('.');
            const logCustomQos = (qosData: IQosEndSchema) => {
                if (customQosLogger) {
                    customQosLogger(qosData);
                }
            };

            let isCanceled: boolean;
            let serviceWorkerDataSource: string;

            /* tslint:disable: no-any */
            let onExecute = (complete: (argData?: T | Blob) => void, error: (err?: any) => void) => {
                /* tslint:enable: no-any */
                // forward declarations to shut up linter
                let onDataSuccess: (serverData: ServerData) => void;
                let onError: (serverData: ServerData) => void;
                let doGetData: () => void;

                onDataSuccess = (serverData: ServerData) => {
                    let data: T | Blob;
                    let qos = new this._qosEvent({ name: qosName });
                    const response = serverData.getResponseText();
                    serviceWorkerDataSource = serverData.getServiceWorkerDataSourceHeader();
                    const extraData = {
                        ...(qosExtraData || {}),
                        CorrelationId: serverData.getCorrelationId(),
                        HttpStatus: serverData.getStatus(),
                        serviceWorkerDataSource: serviceWorkerDataSource || undefined
                    };
                    let parsedResponse = false;
                    try {
                        if (this._isBlobResponse(responseType, response)) {
                            data = response;
                        } else {
                            data = parseResponse(response, serverData);
                        }
                        // This line could throw if the response does not have the expected data type.
                        // For example, there is at least one handler (SpoSuiteLinks.ashx) that will return
                        // an HTML error page rather than the expected JSON on error. We should count that case
                        // as a failure even though *something* was successfully returned.
                        parsedResponse = true;
                        const qosData = {
                            resultType: ResultTypeEnum.Success,
                            extraData: extraData
                        };
                        qos.end(qosData);
                        logCustomQos(qosData);
                    } catch (ex) {
                        // The response did not parse properly.
                        const qosData = {
                            resultType: ResultTypeEnum.Failure,
                            resultCode: 'InvalidJSON',
                            error: (typeof ex === 'object' ? JSON.stringify(ex) : ex),
                            extraData: extraData
                        };
                        qos.end(qosData);
                        logCustomQos(qosData);
                        // let the caller know there was an error in parsing the data
                        error();
                    }

                    // pass the response to the caller
                    if (parsedResponse) {
                        qos = new this._qosEvent({ name: qosName + 'Complete' });
                        try {
                            complete(data);
                            const qosData = {
                                resultType: ResultTypeEnum.Success,
                                extraData: extraData
                            };
                            qos.end(qosData);
                            logCustomQos(qosData);
                        } catch (ex) {
                            // the caller failed to handle the data
                            const qosData = {
                                resultType: ResultTypeEnum.Failure,
                                error: (typeof ex === 'object' ? JSON.stringify(ex) : ex),
                                extraData: extraData
                            };
                            qos.end(qosData);
                            logCustomQos(qosData);
                        }
                    }
                    serverConnection = undefined;
                };

                onError = (serverData?: ServerData) => {
                    const qos = new this._qosEvent({
                        name: qosName
                    });

                    let extraData = qosExtraData || {};
                    if (!serverData) {
                        let qosData;
                        if (isCanceled) {
                            // serverData will be undefined if the request was aborted
                            qosData = {
                                resultType: ResultTypeEnum.ExpectedFailure,
                                resultCode: 'RequestAborted',
                                extraData: extraData
                            };
                        } else {
                            // Request completed, but did not return data or a useful status code. This can happen for any number of reasons:
                            // CORS Rejection (we could make a guess about this by comparing url origins)
                            // Request blocked by client (e.g. extension)
                            // Request blocked by firewall
                            qosData = {
                                resultType: ResultTypeEnum.ExpectedFailure,
                                resultCode: 'Unknown',
                                extraData: extraData
                            };
                            // Ensure that Promises that wrap the data request always complete.
                            error({ status: 0, code: '' });
                        }
                        qos.end(qosData);
                        logCustomQos(qosData);

                        return;
                    }

                    const correlationId = serverData.getCorrelationId();
                    const groupThrottle = serverData.getGroupThrottle();
                    const status = serverData.getStatus();
                    extraData = {
                        ...extraData,
                        CorrelationId: correlationId,
                        HttpStatus: status,
                        GroupThrottle: groupThrottle
                    };

                    let resultType = ResultTypeEnum.Failure;
                    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
                    if (status === 403 || status === 404 || status === 429 || status === 503 || (status === 0 && isOffline)) {
                        // no need to retry authentication errors...
                        // no need to retry document not found errors...
                        // no need to retry resource throttling errors... (we need to back away from it. 503 is SQL throttling)
                        // no need to retry if we are offline
                        maxRetries = 0;
                        resultType = ResultTypeEnum.ExpectedFailure;
                    }

                    let parseQosError = (response: string | IErrorData | Blob): void => {
                        let errorData: IErrorData = undefined;
                        if (typeof response === 'object' && typeof (<IErrorData>response).status === 'number') {
                            // Test to see if the response is already an IErrorData
                            errorData = <IErrorData>response;
                        } else if (typeof response === 'string') {
                            let parsedData = undefined;
                            try {
                                parsedData = JSON.parse(response);
                            }
                            catch (ex) {
                                // np-op
                            }

                            if (parsedData) {
                                if (typeof parsedData.error === 'string') {
                                    if (parsedData.error_description) {
                                        errorData = <IErrorData>{
                                            code: `-1,${parsedData.error}`,
                                            message: parsedData.error_description
                                        };
                                    } else {
                                        errorData = <IErrorData>{
                                            message: parsedData.error
                                        };
                                    }
                                } else {
                                    errorData = <IErrorData>(parsedData.error || parsedData['odata.error'] || { responseData: parsedData });
                                }
                            }
                        }

                        errorData = <IErrorData>(errorData || {});
                        errorData.status = status;
                        if (!Killswitch.isActivated('5202A778-5F5A-492D-B815-CCBDEECC399D', '1/17/2019', 'Add redirectUrl to errors.')) {
                            const errorWithRedirect: {
                                '@error.redirectUrl'?: string;
                            } & IErrorData = errorData;

                            const payloadRedirectUrl = errorWithRedirect['@error.redirectUrl'];

                            const redirectUrl = serverData.getRedirectUrl() || payloadRedirectUrl;

                            if (redirectUrl) {
                                // If there is a process to handle the error, then it's expected.
                                resultType = ResultTypeEnum.ExpectedFailure;
                            }

                            errorData.redirectUrl = redirectUrl;
                        }
                        if (correlationId) {
                            errorData.correlationId = correlationId;
                        }
                        if (groupThrottle) {
                            errorData.groupThrottle = groupThrottle;
                        }

                        let resultCode = status.toString();
                        if (qosHandler) {
                            resultCode = qosHandler(errorData);
                            if (status === 500 && (resultCode === 'DefaultDoclibNotFound' || resultCode === 'PrefetchWebMismatch')) {
                                maxRetries = 0;
                                resultType = ResultTypeEnum.ExpectedFailure;
                            }
                        }

                        let errorMessage: string = JSON.stringify(errorData);

                        const qosData = {
                            resultType: resultType,
                            error: errorMessage,
                            resultCode: resultCode,
                            extraData: extraData
                        };
                        qos.end(qosData);
                        logCustomQos(qosData);

                        if (numRetries < maxRetries) {
                            ++numRetries;
                            doGetData();
                        } else {
                            error(errorData);
                        }
                    };

                    let responseType = serverData.getResponseType();
                    if (!responseType || responseType === 'text') {
                        let responseText = serverData.getResponseText();
                        parseQosError(responseText);
                    } else {
                        serverData.parseError().then(parseQosError);
                    }

                };

                doGetData = () => {
                    serverConnection.getServerDataFromUrl({
                        url: url,
                        successCallback: onDataSuccess,
                        failureCallback: onError,
                        uploadProgressCallback: onUploadProgress,
                        additionalPostData: additionalPostData,
                        method: method,
                        additionalHeaders: additionalHeaders,
                        contentType: contentType,
                        withCredentials: authToken ? false : undefined,
                        noRedirect: noRedirect,
                        responseType: responseType,
                        needsRequestDigest: needsRequestDigest
                    });
                };
                doGetData();
            };

            let onCancel = () => {
                isCanceled = true;
                if (serverConnection) {
                    serverConnection.abort();
                    if (numRetries >= maxRetries) { // do not dispose serverConnection when data requestor is in the middle of retries.
                        serverConnection.dispose();
                    }
                }
            };

            let rumOne: RUMOneLogger = RUMOneLogger.getRUMOneLogger();
            let apiStartTime: number = Date.now();

            if (rumOne) {
                if (!Boolean(rumOne.readTempData('appDataFetchStart'))) {
                    // Record the App DataFetch start before the first API call
                    rumOne.saveTempData('appDataFetchStart', apiStartTime);
                }
            }

            const createPromise: () => CancelablePromise<T | Blob> = () => {
                let promise: CancelablePromise<T | Blob>;
                const createTimeoutPromise: () => CancelablePromise<T | Blob> = () => {
                    const promise = new CancelablePromise<T | Blob>(onExecute, onCancel);
                    // when it is the last retry and noTimeoutInLastRetry is set to true, do not set timeout to allow calls to finish,
                    // we at least can get data no matter how slow
                    if (timeout && (!noTimeoutInLastRetry || numRetries < maxRetries)) {
                        return CancelablePromise.timeout(timeout, promise);
                    }
                    return promise;
                };
                if (numRetries < maxRetries) { //retry promise
                    promise = Retry<T | Blob>({
                        retries: maxRetries,
                        callback: createTimeoutPromise,
                        canRetry: (error: any) => true,
                        async: this._async,
                        delay: 0,
                        adjustRetries: (error: any) => maxRetries - numRetries,
                        beforeRetry: () => {
                            numRetries++;
                        }
                    });
                } else {
                    promise = createTimeoutPromise();
                }
                return promise.then((result: T | Blob) => {
                    if (serverConnection) {
                        serverConnection.dispose();
                    }
                    return result;
                }, (error: any) => {
                    if (serverConnection) {
                        serverConnection.dispose();
                    }
                    return CancelablePromise.reject(error);
                });
            };

            const qosSuccessSchema = (result: T): IApiEndSchema => {
                return <IApiEndSchema>{
                    resultType: ResultTypeEnum.Success,
                    extraData: {
                        serviceWorkerDataSource: serviceWorkerDataSource || undefined
                    }
                };
            }

            return this._apiEvent.instrumentPromise({
                url: url,
                name: qosName + '.Promise'
            }, createPromise, qosSuccessSchema, (error?: IErrorData | Error | string): IQosEndSchema => {
                /* tslint:enable:no-string-literal no-any */
                /// TODO: implement proper cancelation check when it's implemented in Promise.ts
                if (error instanceof Error && CancelablePromise.isCanceled(error)) {
                    return {
                        error: error.message,
                        resultCode: 'Canceled',
                        resultType: ResultTypeEnum.ExpectedFailure
                    };
                }

                if (error && (error as IErrorData).status === 403) {
                    return {
                        error: JSON.stringify(error),
                        resultCode: 'AccessDenied',
                        resultType: ResultTypeEnum.ExpectedFailure
                    };
                }

                return {
                    ...getQosEndSchema(error),
                    resultType: ResultTypeEnum.Failure
                };
            });
        };

        return CancelablePromise.resolve(
            specifiedAuthToken ||
            (pageContext && (
                pageContext.getAuthToken && pageContext.getAuthToken() ||
                pageContext.authToken))
        ).then(getDataPromise);
    }

    private _isBlobResponse(responseType: string, response: string | Blob): response is Blob {
        return responseType === 'blob' && response && typeof response === 'object';
    }
}

function getQosEndSchema(error: IErrorData | Error | string): Pick<IQosEndSchema, 'resultCode' | 'error'> {
    if (Killswitch.isActivated('78C04DFD-1688-4679-A984-ED60A4341F51', '4/10/2018', 'Include error data in QoS failures.')) {
        return {
            resultCode: '',
            error: `${error}`
        };
    }

    if (error instanceof Error) {
        return {
            resultCode: '',
            error: error.toString()
        };
    }

    if (isErrorData(error)) {
        return {
            resultCode: typeof error.code === 'string' ? error.code : `${error.status}`,
            error: JSON.stringify(error)
        };
    }

    return {
        error: error,
        resultCode: ''
    };
}

function isErrorData(error: IErrorData | string): error is IErrorData {
    return !!error && typeof error === 'object' && ('code' in (error as IErrorData));
}

export default DataRequestor;
